import { useState, useEffect, useRef, useReducer } from 'react'
import { Button, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { UploadFileRounded, FileCopyRounded } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import Dropzone, { IDropzoneProps } from 'react-dropzone-uploader'
import { fsColors } from '../../../shared/constants'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getFileMappings,
  goBackETLStep,
  setEtlFiles,
  setLatestEtlStatus,
} from '../../../store/actions'
import FsContainer from '../common/Container'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'
import { useLoading } from '../../../shared/hooks'
import ACTIONS from '../../../store/constants'
import { ReactComponent as UploadIcon } from '../../../assets/etl/cloud_icon.svg'
import FsFileMappings from '../common/FileMappings'
import { etlAPI } from '../../../shared/api/ETLAPI'

const FsMergeFiles = ({ setIsLoading }: { setIsLoading: (isLoading: boolean) => void }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { latestEtlStatus } = useAppSelector((state) => state.currentEtl)
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const alertRef = useRef<FsAlertRef>(null)
  const [, forceUpdate] = useReducer(x => x + 1, 0)

  useEffect(() => {
    if (latestEtlStatus) {
      dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
      dispatch(getFileMappings())
    }
  }, [latestEtlStatus])

  const { isFetching } = useLoading([
    ACTIONS.ETL.SET_ETL_STATUS,
    ACTIONS.ETL.SET_ETL_FILES,
    ACTIONS.ETL.GET_FILE_MAPPINGS,
  ])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  const handleAccept = async () => {
    if (latestEtlStatus)
      try {
        setIsLoading(true)
        const result: any = await etlAPI.processFiles(latestEtlStatus.id)
        if (result?.status === 'True') {
          alertRef.current?.openAlert({
            message: t('download.processed.file'),
          })
          dispatch(setLatestEtlStatus())
        } else
          alertRef.current?.openAlert({
            severity: 'error',
            message: t('something.went.wrong'),
          })
      } catch (error) {
        alertRef.current?.openAlert({
          severity: 'error',
          message: t('something.went.wrong'),
        })
      } finally {
        setIsLoading(false)
      }
  }

  const handleSubmit: IDropzoneProps['onSubmit'] = (_, allFiles) => {
    if (latestEtlStatus) {
      setIsLoading(true)
      const { file } = allFiles[0]
      etlAPI
        .uploadProcessedFile(file, latestEtlStatus.id)
        .then((res) => {
          if (res.status === 'True') {
            dispatch(setLatestEtlStatus())
            alertRef.current?.openAlert({
              message: t('file.upload.success'),
            })
            allFiles.forEach((f) => f.remove())
          } else {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.error'),
            })
          }
        })
        .catch((err) => {
          if (err.status === 413) {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.toobig.etl'),
            })
          } else {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.error'),
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  return (
    <FsContainer
      footer={
        !isOpen ? (
          <Stack width="100%" direction="row" justifyContent="space-between">
            <Stack>
              <Button onClick={() => dispatch(goBackETLStep())}>{t('back')}</Button>
            </Stack>
            <Stack direction="row" gap={1}>
              <Button
                onClick={() => setIsOpen(true)}
                startIcon={<UploadFileRounded />}
                variant="outlined"
              >
                {t('files.process.upload')}
              </Button>
              <Button
                startIcon={<FileCopyRounded />}
                disabled={isOpen}
                onClick={handleAccept}
                variant="contained"
              >
                {t('files.process.upload.continue')}
              </Button>
            </Stack>
          </Stack>
        ) : null
      }
    >
      <Stack>
        {isOpen ? (
          <Stack alignItems="flex-end">
            <IconButton onClick={() => setIsOpen(false)}>
              <CloseIcon />
            </IconButton>
            <Dropzone
              onSubmit={handleSubmit}
              accept="text/csv,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              styles={{
                dropzone: {
                  marginTop: 8,
                  minHeight: 200,
                  paddingLeft: 20,
                  paddingRight: 20,
                },
                inputLabel: {
                  padding: '16px 172px',
                  color: theme.palette.primary.main,
                  fontSize: 16,
                  textAlign: 'center',
                },
                preview: {
                  backgroundColor: fsColors.fsDarkBlue.colors[3],
                  marginTop: 16,
                  borderRadius: 4,
                },
                submitButton: {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.paper,
                  padding: '12px 24px',
                },
                inputLabelWithFiles: {
                  border: `1px solid ${theme.palette.primary.main}`,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  padding: '8px 16px',
                  marginLeft: '0px',
                },
              }}
              submitButtonContent={t('file.upload.submit')}
              multiple={false}
              maxFiles={1}
              inputContent={
                <Stack alignItems="center">
                  <UploadIcon />
                  <Typography variant="h6" textAlign="center" mt={2}>
                    {t('file.drag.drop')}
                  </Typography>
                </Stack>
              }
              onChangeStatus={(_, status) => {
                if (status === 'rejected_file_type') {
                  alertRef.current?.openAlert({
                    message: t('file.upload.toobig.etl'),
                    severity: 'error',
                  })
                } else {
                  forceUpdate()
                }
              }}
            />
          </Stack>
        ) : (
          <>
            <Typography my={2}>{t('files.process.description')}</Typography>
            <FsFileMappings setIsLoading={setIsLoading} isReadOnly />
          </>
        )}
      </Stack>
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsMergeFiles
